import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { money, MODE_META, STATUS_META, PAYMENT_META } from "@/lib/pricing";
import { Navbar } from "@/components/Navbar";
import { StatusTracker } from "@/components/StatusTracker";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MapPin, Phone, CalendarDays, Truck, CakeSlice } from "lucide-react";
import { toast } from "sonner";

const BalanceCard = ({ order }) => {
  const [busy, setBusy] = useState(false);
  if (!(order.balance_due > 0) || order.payment_status === "pending") return null;
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/pay-balance`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <div data-testid="order-balance-card" className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-yellow-400 bg-yellow-200 p-5">
      <div>
        <div className="font-display text-lg font-bold text-sugar-ink">Balance due: {money(order.balance_due)}</div>
        <p className="text-sm text-sugar-ink2">Your order was updated. Tax is included.</p>
      </div>
      <Button data-testid="order-pay-balance" disabled={busy} onClick={pay} className="rounded-full bg-sugar-ink px-6 font-bold text-white hover:bg-sugar-ink/90">{busy ? "Opening…" : "Pay balance"}</Button>
    </div>
  );
};

export default function OrderDetail() {
  const { id } = useParams();
  const { user, checked } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (checked && !user) { navigate("/login", { state: { next: `/track/${id}` } }); return; }
    if (user) api.get(`/orders/${id}`).then(({ data }) => setOrder(data)).catch(() => setNotFound(true));
  }, [checked, user, id, navigate]);

  if (notFound) return (<div className="App"><Navbar /><div className="py-24 text-center text-sugar-ink2">Order not found.</div></div>);
  if (!order) return (<div className="App"><Navbar /><div className="py-24 text-center text-sugar-ink2">Loading…</div></div>);

  return (
    <div className="App min-h-screen bg-sugar-bg"><Navbar />
      <div className="mx-auto max-w-3xl px-6 py-10 lg:px-8">
        <button onClick={() => navigate("/track")} className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-sugar-ink2 hover:text-sugar-pink"><ArrowLeft size={16} /> All orders</button>

        <div className="rounded-2xl border border-sugar-border bg-white p-6 sugar-card-shadow">
          <div className="flex items-center justify-between">
            <h1 className="font-display text-2xl font-extrabold text-sugar-ink">Order #{order.id.slice(-6).toUpperCase()}</h1>
            <span className="rounded-full px-3 py-1 text-xs font-bold text-white" style={{ background: STATUS_META[order.status]?.color }}>{STATUS_META[order.status]?.label}</span>
          </div>
          <p className="mt-1 text-sm text-sugar-ink2">{MODE_META[order.mode]?.label} · {order.fulfillment_date}</p>
          {order.status === "placed" && <p data-testid="order-placed-note" className="mt-3 rounded-xl bg-sugar-cream px-3 py-2 text-sm text-sugar-ink2">Thanks! Sarah will mark your order as received as soon as she's seen it.</p>}
          <div className="mt-8"><StatusTracker status={order.status} /></div>
        </div>
        <BalanceCard order={order} />

        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-sugar-border bg-white p-6">
            <h3 className="font-display text-lg font-bold text-sugar-ink">Items</h3>
            <div className="mt-4 space-y-4">
              {order.items.map((it, i) => (
                <div key={i} className="flex gap-3">
                  {it.image ? <img src={it.image} alt="" className="h-14 w-14 rounded-xl object-cover" /> : <span className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-sugar-blush text-sugar-pink"><CakeSlice size={20} /></span>}
                  <div className="flex-1">
                    <div className="flex justify-between"><span className="font-semibold text-sugar-ink">{it.style_name}</span><span className="font-bold text-sugar-pink">{money(it.unit_price)}</span></div>
                    <p className="text-xs text-sugar-ink2">{Object.entries(it.selections).filter(([, v]) => v && (!Array.isArray(v) || v.length)).map(([, v]) => (Array.isArray(v) ? v.join(", ") : v)).join(" · ")}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 space-y-1.5 border-t border-sugar-border pt-4 text-sm">
              <div className="flex justify-between text-sugar-ink2"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
              <div className="flex justify-between text-sugar-ink2"><span>Delivery</span><span>{order.delivery_fee === 0 ? "FREE" : money(order.delivery_fee)}</span></div>
              {order.rush_fee > 0 && <div className="flex justify-between text-sugar-ink2"><span>Rush fee</span><span>{money(order.rush_fee)}</span></div>}
              <div className="flex justify-between text-sugar-ink2"><span>GST + PST (12%)</span><span>{money(order.tax || 0)}</span></div>
              {order.tip > 0 && <div data-testid="order-tip" className="flex justify-between text-sugar-ink2"><span>Tip{order.tip_percent ? ` (${order.tip_percent}%)` : ""}</span><span>{money(order.tip)}</span></div>}
              <div className="flex justify-between font-display text-lg font-bold text-sugar-ink"><span>Total</span><span className="text-sugar-pink">{money(order.total)}</span></div>
              {(order.refunds || []).map((r, i) => (
                <div key={i} data-testid={`order-refund-${i}`} className="flex justify-between text-sugar-ink2"><span>Refund{r.reason ? ` · ${r.reason}` : ""}</span><span>-{money(r.amount)}</span></div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-sugar-border bg-white p-6">
            <h3 className="font-display text-lg font-bold text-sugar-ink">Fulfillment</h3>
            <ul className="mt-4 space-y-3 text-sm text-sugar-ink2">
              <li className="flex items-center gap-2.5"><Truck size={16} className="text-sugar-pink" /> {order.delivery_method === "pickup" ? "Pickup in Langley" : "Delivery"}</li>
              <li className="flex items-center gap-2.5"><CalendarDays size={16} className="text-sugar-pink" /> {order.fulfillment_date}</li>
              {order.address && <li className="flex items-center gap-2.5"><MapPin size={16} className="text-sugar-pink" /> {order.address}</li>}
              <li className="flex items-center gap-2.5"><Phone size={16} className="text-sugar-pink" /> {order.customer_phone}</li>
            </ul>
            {order.notes && <p className="mt-4 rounded-xl bg-sugar-cream px-3 py-2 text-sm text-sugar-ink2">"{order.notes}"</p>}
            <div data-testid="order-payment-status" className="mt-4 text-xs font-semibold uppercase tracking-widest" style={{ color: (PAYMENT_META[order.payment_status] || PAYMENT_META.pending).color }}>
              Payment: {(PAYMENT_META[order.payment_status] || PAYMENT_META.pending).label}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
